import type { SessionRecordingOptions } from "posthog-js";

/**
 * Reverse-proxy origin that fronts PostHog. Shared so the CSP allowlist and the PostHog
 * client can never drift: if this moves and only one side is updated, the enforced
 * payment-page policy blocks PostHog in production instead of warning.
 */
export const POSTHOG_API_HOST = "https://z.nowadays.ai";

/**
 * Session recording options for the PostHog client. Inputs are masked because attendees type
 * their names and emails into public forms without signing in, so those recordings have no
 * person profile and cannot be deleted on a data subject request.
 */
export const POSTHOG_SESSION_RECORDING: SessionRecordingOptions = { maskAllInputs: true };

/** Google Analytics 4 tracking ID */
export const GOOGLE_ANALYTICS_ID = "G-DX27KPVBFF";

/** Google Ads conversion tracking ID */
export const GOOGLE_ADS_CONVERSION_ID = "AW-18023528265";

/** Google Ads conversion label for the "Submit lead form" conversion action */
export const LEAD_FORM_CONVERSION_LABEL = "7Et4CJ7J24ocEMnupJJD";

/** All tracking IDs to configure with gtag */
export const GTAG_TRACKING_IDS = [GOOGLE_ANALYTICS_ID, GOOGLE_ADS_CONVERSION_ID] as const;

/**
 * Route prefixes that must never be sent to third-party analytics. The OAuth consent
 * page (`/oauth/consent`) carries an `authorization_id` in its URL and renders the
 * account email + requesting-client host in its DOM; capturing any of that leaks the
 * consent binding and PII. A contract signing link (`/sign/<token>`) is the signer's
 * whole credential, and the page shows their drawn signature. A contract invite link
 * (`/contract-invite/<token>`) carries its token, and the page shows the invited email and
 * event. PostHog page-view + session recording, GA/Ads page_view, and Snitcher are all
 * suppressed for these paths, and on any page whose `redirect` leads to one
 * ({@link redirectsToSensitivePath}).
 */
export const SENSITIVE_ANALYTICS_PATH_PREFIXES = ["/oauth/consent", "/sign", "/contract-invite"] as const;

/** Query param that sign-in and sign-out carry the page to come back to in. */
const REDIRECT_QUERY_PARAM = "redirect";

/** Base for parsing relative URLs; only path and query are ever read back. */
const URL_PARSE_BASE = "http://localhost";

/**
 * Whether a pathname is an analytics-sensitive route that must be excluded from all
 * third-party capture. Matches the prefix exactly or at a path-segment boundary
 * (`/oauth/consent` and `/oauth/consent/...`, but not `/oauth/consent-other`).
 *
 * @param pathname - The current path without query string (e.g. `window.location.pathname`).
 * @returns True if analytics must be suppressed for this path.
 */
export function isSensitiveAnalyticsPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  return SENSITIVE_ANALYTICS_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/**
 * Whether a URL's `redirect` param leads to an analytics-sensitive route, directly or through a
 * nested `redirect` (sign-out → sign-in → invite). Sign-in for an invite link is
 * `/login?redirect=%2Fcontract-invite%2F<token>`, so that page's URL holds the token too.
 *
 * @param url - A full or relative URL, or just its query string (`?redirect=...`).
 * @returns True if the URL carries a sensitive route's path in its `redirect`.
 */
export function redirectsToSensitivePath(url: string): boolean {
  if (!url.includes("?")) return false;
  try {
    const target = new URL(url, URL_PARSE_BASE).searchParams.get(REDIRECT_QUERY_PARAM);
    if (target === null) return false;
    return isSensitiveAnalyticsPath(new URL(target, URL_PARSE_BASE).pathname) || redirectsToSensitivePath(target);
  } catch {
    return false;
  }
}

/**
 * Route prefixes where the third-party tags (GA/Ads and Snitcher) stay off, on top of
 * {@link SENSITIVE_ANALYTICS_PATH_PREFIXES}. `/admin` is internal staff tooling with no marketing
 * value, and its URLs carry staff searches (the command palette's `q` hand-off can hold names and
 * emails). PostHog still tracks admin pages; it strips `q` through {@link SENSITIVE_QUERY_PARAMS}.
 */
export const THIRD_PARTY_SUPPRESSED_PATH_PREFIXES = [...SENSITIVE_ANALYTICS_PATH_PREFIXES, "/admin"] as const;

/**
 * Whether a pathname keeps the third-party tags (GA/Ads and Snitcher) off: an analytics-sensitive
 * route or any `/admin` route. Matches at a path-segment boundary, like
 * {@link isSensitiveAnalyticsPath}.
 *
 * @param pathname - The current path without query string.
 * @returns True if GA/Ads and Snitcher must not run for this path.
 */
export function isThirdPartySuppressedPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  return THIRD_PARTY_SUPPRESSED_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

/**
 * Emits an inline-JS expression (as a string) that evaluates {@link isThirdPartySuppressedPath}
 * against the live `location.pathname`, or {@link redirectsToSensitivePath} against
 * `location.search`, at script-eval time. Needed because GA/Ads and Snitcher
 * suppression run in inline `<head>` scripts that can't import this module. Co-located with the
 * TS predicate and equivalence-tested so the two encodings can't silently drift.
 *
 * @returns A JS boolean expression reading `location` (true where the tags stay off).
 */
export function buildSensitivePathCheckJs(): string {
  const base = toInlineJsLiteral(URL_PARSE_BASE);
  return `(function(T,S){function m(P,p){return P.some(function(x){return p===x||p.indexOf(x+"/")===0;});}function r(u){if(u.indexOf("?")<0)return false;try{var t=new URL(u,${base}).searchParams.get(${toInlineJsLiteral(
    REDIRECT_QUERY_PARAM
  )});return t!==null&&(m(S,new URL(t,${base}).pathname)||r(t));}catch(e){return false;}}return m(T,location.pathname)||r(location.search);})(${toInlineJsLiteral(
    THIRD_PARTY_SUPPRESSED_PATH_PREFIXES
  )},${toInlineJsLiteral(SENSITIVE_ANALYTICS_PATH_PREFIXES)})`;
}

/** Characters that can end a `<script>` block or a JS string when JSON is inlined into HTML. */
const INLINE_SCRIPT_UNSAFE_CHARS = /[<>/\u2028\u2029]/g;

/**
 * JSON-encodes a value for interpolation into an inline `<script>`, `\u`-escaping the
 * characters that could close the script tag or break the string literal.
 *
 * @param value - Any JSON-serializable value.
 * @returns A JS literal safe to embed in inline script source.
 */
function toInlineJsLiteral(value: unknown): string {
  return JSON.stringify(value).replace(
    INLINE_SCRIPT_UNSAFE_CHARS,
    (char) => `\\u${char.charCodeAt(0).toString(16).padStart(4, "0")}`
  );
}

/**
 * Query param names that must never appear in a captured page-view URL, on ANY route
 * (unlike {@link SENSITIVE_ANALYTICS_PATH_PREFIXES}, which suppresses a whole path).
 * `t` is the forms same-session edit token (see response-tokens.ts) — a non-expiring
 * bearer credential that authorizes reading/overwriting a response, carried in the URL
 * so a bookmarked or emailed confirmation link keeps working. It must never be captured
 * by PostHog (or any other tool reading the page URL), even though the rest of that
 * page's analytics (view counts, funnels) is legitimate and should still be tracked.
 * `q` is the admin command palette's hand-off query ("Search all events for 'x'"), which
 * seeds the All events and Users lists. It carries the raw query, which can hold names and
 * emails, so it must not reach a captured URL either. The palette's own analytics send only a
 * redacted copy, and only on a miss (`query_redacted` on `search_empty` and `search_abandoned`); stripping `q` is what
 * keeps the raw query out of every other event's URL.
 */
export const SENSITIVE_QUERY_PARAMS = ["t", "q"] as const;

/**
 * Strips {@link SENSITIVE_QUERY_PARAMS} from a URL before it's handed to an analytics
 * capture call, and its `redirect` when that leads to a sensitive route
 * ({@link redirectsToSensitivePath}). Every other query param and the path are left untouched.
 *
 * Runs via PostHog's `sanitize_properties` on every captured event (autocapture, session
 * recording, manual captures) — the no-query-string fast path matters, since almost every
 * URL takes it.
 *
 * @param url - A full or relative URL string
 * @returns The same URL with sensitive params removed, or the original string unchanged
 *   if it isn't parseable
 */
export function stripSensitiveQueryParams(url: string): string {
  if (!url.includes("?")) return url;
  try {
    const parsed = new URL(url, "http://localhost");
    let changed = false;
    for (const param of SENSITIVE_QUERY_PARAMS) {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.delete(param);
        changed = true;
      }
    }
    if (redirectsToSensitivePath(url)) {
      parsed.searchParams.delete(REDIRECT_QUERY_PARAM);
      changed = true;
    }
    if (!changed) return url;
    // Reconstruct without an absolute origin when the input was relative.
    return url.startsWith("/") ? `${parsed.pathname}${parsed.search}` : parsed.toString();
  } catch {
    return url;
  }
}

/** Stands in for the credential segment of a sensitive path in any captured URL. */
const REDACTED_PATH_SEGMENT = "[redacted]";

/**
 * Replaces everything after a sensitive prefix with {@link REDACTED_PATH_SEGMENT}, so
 * `/contract-invite/<token>` becomes `/contract-invite/[redacted]`. Other paths pass through.
 *
 * @param pathname - A path without query string.
 * @returns The path with its credential segment redacted.
 */
function redactSensitivePath(pathname: string): string {
  const prefix = SENSITIVE_ANALYTICS_PATH_PREFIXES.find((p) => pathname.startsWith(`${p}/`));
  if (!prefix || pathname.length === prefix.length + 1) return pathname;
  return `${prefix}/${REDACTED_PATH_SEGMENT}`;
}

/** The backend's invite routes carry the same token, and server-side fetch breadcrumbs record them. */
const BACKEND_INVITE_TOKEN_PATH = /(\/contracting\/participant-invites\/)[^/?#]+/;

/**
 * Scrubs a URL or bare pathname before it reaches any third party (PostHog, Sentry): strips
 * sensitive query params ({@link stripSensitiveQueryParams}) and redacts the token segment of a
 * sensitive path. Runs on every captured event, so it skips parsing when no sensitive path occurs.
 *
 * @param url - A full or relative URL, or a pathname.
 * @returns The scrubbed URL, or the input unchanged if nothing sensitive is in it.
 */
export function scrubAnalyticsUrl(url: string): string {
  const stripped = stripSensitiveQueryParams(url).replace(BACKEND_INVITE_TOKEN_PATH, `$1${REDACTED_PATH_SEGMENT}`);
  if (!SENSITIVE_ANALYTICS_PATH_PREFIXES.some((p) => stripped.includes(`${p}/`))) return stripped;
  try {
    const parsed = new URL(stripped, URL_PARSE_BASE);
    const pathname = redactSensitivePath(parsed.pathname);
    if (pathname === parsed.pathname) return stripped;
    parsed.pathname = pathname;
    return stripped.startsWith("/") ? `${parsed.pathname}${parsed.search}${parsed.hash}` : parsed.toString();
  } catch {
    return stripped;
  }
}

/** The quote-submit claim popup's PostHog events, shared by the popup and the admin page. */
export const PORTAL_CLAIM_EVENTS = {
  popupShown: "portal_claim_popup_shown",
  continueClicked: "portal_claim_continue_clicked",
} as const;

/**
 * Name of the httpOnly session cookie `impersonateUser` sets, holding the `auth.users.id` of the
 * login a staff member is impersonating. The root layout compares it with the current login to
 * decide whether the session is impersonated; it never reaches the browser's JavaScript.
 */
export const IMPERSONATION_COOKIE_NAME = "nw_impersonated_login";

/**
 * How long the impersonation cookie lives, in seconds. Short so a marker left behind (the browser
 * stays open after sign-out) cannot outlast a working session and mislabel a later login.
 */
export const IMPERSONATION_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 8;

/** The event property `PostHogPageView` registers while a session is impersonated. */
export const IMPERSONATION_EVENT_PROPERTY = "is_impersonated";

/**
 * Exception messages thrown by scripts a host browser or app injects into every page it opens, not
 * by our code. Each one references the host's own native bridge, which is missing when the script
 * runs, so the error lands on whatever page the user had open (e.g. `/bts-america:1`). Snapchat's
 * iOS webview calls `SCDynimacBridge` (its spelling), Brave iOS calls `__firefox__`, and Instagram's
 * iOS webview calls `window.webkit.messageHandlers`.
 */
export const INJECTED_SCRIPT_EXCEPTION_PATTERNS: readonly RegExp[] = [
  /\bSCDynimacBridge\b/,
  /\b__firefox__\b/,
  /\bwebkit\.messageHandlers\b/,
];
